import React, { useMemo, useCallback } from "react"
import { useFormContext } from "react-hook-form"
import { useQueryClient } from "@tanstack/react-query"
import { SelectField } from "@mimber/forms"
import { Space, Button } from "@mimber/ui-web"
import { PlusOutlined, EyeOutlined } from "@mimber/ui-web"
import {
  useCommunicationTemplates,
  communicationTemplatesKeys,
} from "@mimber/client/mimber-organization"
import { QuickCreateCommunicationTemplateModal } from "@/components/modals/QuickCreateCommunicationTemplateModal"
import { CommunicationTemplatePreviewModal } from "@/components/modals/CommunicationTemplatePreviewModal"
import type { CommunicationTemplateDto } from "@mimber/schema"
import type { CommunicationTemplateSelectProps } from "./CommunicationTemplateSelect.types"

export const CommunicationTemplateSelect: React.FunctionComponent<
  CommunicationTemplateSelectProps
> = ({
  name,
  label = "Template",
  placeholder = "Select a template",
  required = false,
  channelFilter,
  filterFn,
  help,
  disabled = false,
  showQuickCreate = false,
}) => {
  const { data, isLoading, error } = useCommunicationTemplates()
  const queryClient = useQueryClient()
  const { setValue, watch } = useFormContext()

  const selectedValue = watch(name) as string | undefined

  const handleQuickCreate = useCallback(
    (template: CommunicationTemplateDto) => {
      void queryClient.invalidateQueries({
        queryKey: communicationTemplatesKeys.findAll(),
      })
      setValue(name, template.uuid, { shouldValidate: true })
    },
    [queryClient, setValue, name]
  )

  const fieldLabel = (
    <Space size={4}>
      {label}
      {showQuickCreate && channelFilter && (
        <QuickCreateCommunicationTemplateModal
          channel={channelFilter}
          onSuccess={handleQuickCreate}
          renderButton={(open) => (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={open}
              disabled={disabled || isLoading}
              style={{ padding: 0, height: "auto", lineHeight: "inherit" }}
            >
              Create New
            </Button>
          )}
        />
      )}
      {selectedValue && (
        <CommunicationTemplatePreviewModal
          templateUuid={selectedValue}
          renderButton={(open) => (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={open}
              style={{ padding: 0, height: "auto", lineHeight: "inherit" }}
              data-testid="template-preview-button"
            />
          )}
        />
      )}
    </Space>
  )

  const options = useMemo(() => {
    if (!data?.data) return []

    let templates = data.data

    if (channelFilter) {
      templates = templates.filter((t) => t.channel === channelFilter)
    }

    if (filterFn) {
      templates = templates.filter(filterFn)
    }

    return templates.map((t) => ({
      label: `${t.name} (${t.channel})`,
      value: t.uuid,
    }))
  }, [data, channelFilter, filterFn])

  if (error) {
    return (
      <SelectField
        name={name}
        label={fieldLabel}
        placeholder="Failed to load templates"
        disabled
        help="Unable to load templates"
        options={[]}
      />
    )
  }

  return (
    <SelectField
      name={name}
      label={fieldLabel}
      placeholder={placeholder}
      required={required}
      help={help}
      disabled={disabled || isLoading}
      loading={isLoading}
      options={options}
      allowClear
    />
  )
}
